export interface AiConversationSummary {
  id: string;
  title: string | null;
  source: string;
  model: string | null;
  email: string | null;
  tendraUserId: string | null;
  tendraUserName: string | null;
  tendraUserAvatarId: string | null;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  startedBeforeRecording: boolean;
  messageCount: number;
}

export interface AiAttachmentSummary {
  id: string;
  externalMessageId: string;
  filename: string;
  declaredType: string | null;
  sizeBytes: number | null;
}

export interface AiConversationDetail {
  conversation: AiConversationSummary;
  attachments: AiAttachmentSummary[];
}

export interface RecordedBlock {
  type: string;
  text?: string;
  name?: string;
  input?: string;
  is_error?: boolean;
  content?: { type: string; text?: string }[];
}

export interface RecordedMessage {
  id: string;
  role?: string;
  created_at?: string;
  content?: RecordedBlock[];
}

export type MessagePart =
  | { kind: "text"; text: string }
  | { kind: "tool"; label: string; detail: string; isError: boolean };

export interface AiConversationMessages {
  messages: RecordedMessage[];
  nextCursor: string | null;
  unavailable: string | null;
}

export interface MessageGroup {
  key: string;
  fromUser: boolean;
  messages: RecordedMessage[];
  startsAt: string | null;
  showTime: boolean;
}

const TIME_GAP_MS = 30 * 60 * 1000;

export function displayTitle(recording: AiConversationSummary): string {
  return (
    recording.title ??
    (recording.source === "claude_code" ? "Untitled session" : "Untitled chat")
  );
}

export function sourceLabel(source: string): string {
  return source === "claude_code" ? "Claude Code" : "Claude";
}

const OPENING_TAG = /^\s*<([A-Za-z][\w:.-]*)(\s[^>]*)?>\s*$/;

function taggedBlocks(text: string): MessagePart[] {
  const lines = text.split("\n");
  const parts: MessagePart[] = [];
  let loose: string[] = [];

  const flushLoose = () => {
    const joined = loose.join("\n").trim();

    if (joined) parts.push({ kind: "text", text: joined });

    loose = [];
  };

  for (let index = 0; index < lines.length; index++) {
    const tag = OPENING_TAG.exec(lines[index]!)?.[1];
    const end = tag
      ? lines.findIndex((line, at) => at > index && line.trim() === `</${tag}>`)
      : -1;

    if (!tag || end === -1) {
      loose.push(lines[index]!);
      continue;
    }

    flushLoose();
    parts.push({
      kind: "tool",
      label: tag,
      detail: lines
        .slice(index + 1, end)
        .join("\n")
        .trim(),
      isError: false,
    });
    index = end;
  }

  flushLoose();

  return parts;
}

export function isFromUser(message: RecordedMessage, source: string): boolean {
  if (message.role === "assistant") {
    return false;
  }

  return messageParts(message, source).some((part) => part.kind === "text");
}

export function messageParts(
  message: RecordedMessage,
  source: string,
): MessagePart[] {
  const blocks = message.content ?? [];

  if (blocks.length === 0) {
    return [{ kind: "text", text: "[No content]" }];
  }

  return blocks.flatMap((block): MessagePart[] => {
    if (block.type === "text" && block.text !== undefined) {
      return source === "claude_code"
        ? taggedBlocks(block.text)
        : [{ kind: "text", text: block.text }];
    }

    if (block.type === "tool_use") {
      return [
        {
          kind: "tool",
          label: `Used ${block.name ?? "a tool"}`,
          detail: block.input ?? "",
          isError: false,
        },
      ];
    }

    if (block.type === "tool_result") {
      return [
        {
          kind: "tool",
          label: `${block.name ?? "Tool"} ${block.is_error ? "failed" : "result"}`,
          detail: (block.content ?? [])
            .map((item) => item.text ?? "")
            .join("\n"),
          isError: Boolean(block.is_error),
        },
      ];
    }

    return [
      {
        kind: "text",
        text: `[${block.type.replaceAll("_", " ")} not shown]`,
      },
    ];
  });
}

export function groupMessages(
  messages: RecordedMessage[],
  source: string,
): MessageGroup[] {
  const groups: MessageGroup[] = [];
  let previousTime: number | null = null;

  for (const message of messages) {
    const fromUser = isFromUser(message, source);
    const time = message.created_at ? Date.parse(message.created_at) : null;
    const pause =
      previousTime === null ||
      (time !== null && time - previousTime >= TIME_GAP_MS);
    const last = groups.at(-1);

    if (last && last.fromUser === fromUser && !pause) {
      last.messages.push(message);
    } else {
      groups.push({
        key: message.id,
        fromUser,
        messages: [message],
        startsAt: message.created_at ?? null,
        showTime: pause,
      });
    }

    previousTime = time ?? previousTime;
  }

  return groups;
}

export function attachmentKind(attachment: AiAttachmentSummary): string {
  switch (attachment.declaredType) {
    case "application/pdf":
      return "PDF";
    case "image/png":
      return "PNG image";
    case "image/jpeg":
      return "JPEG image";
    case "image/gif":
      return "GIF image";
    case "image/webp":
      return "WebP image";
    case "text/plain":
      return "Text";
    default:
      return "File";
  }
}
